import React from 'react';
import {
  Box, Button, Card, Grid, Typography,
} from '@mui/material';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { CategoriesKeys, UserMissionCategoryPopulated, UserMissionsStatuses } from '@gamiphy/bl/mission';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { DisplayStringTrans, useTranslation } from '@gamiphy/react-i18n';
import { useMissionHandler } from '@gamiphy/loyalty-station-desktop/modules/missions';
import { playButtonSound } from '@gamiphy/loyalty-station-desktop/sounds';
import { UnavowedPopup } from '@gamiphy/loyalty-station-desktop/components/popups/unavowed';
import { Points } from '@gamiphy/loyalty/points';
import { observer } from 'mobx-react';
import { MissionTitle } from '@gamiphy/loyalty/missions';
import { LevelStore } from '@gamiphy/loyalty-station-desktop/store/level';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { useProductsApi } from '@gamiphy/loyalty-station-desktop/hooks/apis';

export type Props = {
  userMission: UserMissionCategoryPopulated,
}

const ParallelMissionContent: React.FC<Props> = observer(({ userMission }) => {
  const [popupOpen, setPopupOpen] = React.useState(false);
  const [icon, setIcon] = React.useState<string>();
  const { t } = useTranslation();

  const missionHandler = useMissionHandler(userMission);
  const productApi = useProductsApi();

  const onMissionCtaClick = React.useCallback(() => {
    if (UserStore.isRealUser) {
      void playButtonSound();
      if (userMission.mission.cta?.action) {
        missionHandler?.action(userMission);
      }
    } else {
      setPopupOpen(true);
    }
  }, [UserStore.isRealUser, userMission.mission.cta?.action, missionHandler]);

  React.useEffect(() => {
    if (AppStore.app) {
      switch (userMission.mission.category.key) {
        case CategoriesKeys.specificProduct:
          if (userMission.mission.requirements.constraints?.[0].value) {
            void productApi.productsControllerGetByExternalId(AppStore.app.id, userMission.mission.requirements.constraints?.[0].value).then((response) => {
              setIcon(response.data.product.images[0]);
            });
          }
          break;
        default:
            // do-nothing
      }
    }
  }, [userMission, AppStore.app]);

  return (
    <Card sx={{
      margin: 0,
      width: '100%',
      boxShadow: 'none',
      borderRadius: '12px',
      border: '1px solid #F2F2F2',
      ...(icon && {
        pr: 1,
      }),
      ...(!icon && {
        pl: 1,
      }),
    }}
    >
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        wrap="nowrap"
        columnSpacing={1}
      >
        {icon && (
        <Grid maxHeight={80}>
          <Box
            width={80}
            height={80}
            component="img"
            src={icon}
            sx={{
              objectFit: 'cover',
              borderRadius: '12px',
            }}
          />
        </Grid>
        )}
        <Grid
          sx={{
            flex: 2,
          }}
          container
          direction="column"
        >
          <Grid size={12}>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: '1.2rem',
                fontWeight: 500,
                lineHeight: 1.25,
                mb: 0.4,
                width: 'fit-content',
                maxWidth: '100%',
              }}
            >
              <MissionTitle mission={userMission.mission} category={userMission.mission.category} />
            </Typography>
          </Grid>
          <Grid size={12} container>
            <Points points={userMission.mission.points} tierConfig={LevelStore.tierConfig} />
          </Grid>
        </Grid>
        {
            userMission.status === UserMissionsStatuses.completed
              ? (
                <CheckCircleOutlineOutlinedIcon color="success" fontSize="large" />
              )
              : (
                userMission.mission.cta && !missionHandler?.hideButton && (
                <Grid>
                  <Button
                    sx={{
                      width: '8rem',
                      fontSize: '1rem',
                    }}
                    variant="contained"
                    color="primary"
                    onClick={onMissionCtaClick}
                  >
                    <DisplayStringTrans displayString={userMission.mission.cta.label} />
                  </Button>
                  <UnavowedPopup
                    open={popupOpen}
                    onClose={() => setPopupOpen(false)}
                    title={t('unavowedPopup.rewardTitle', 'Sign up now to redeem your coupon for exclusive discounts!')}
                  />
                </Grid>
                )
              )
          }
      </Grid>
    </Card>
  );
});

export default ParallelMissionContent;
